/**
 * Turning SL's timestamps into the text on a departure row.
 *
 * The board used to render SL's own `display` field ("Nu", "14 min") and was
 * right to: no arithmetic, no timezone to get wrong. The catch is that a
 * pre-rendered countdown is only true at the moment it was fetched, so
 * keeping it honest means refetching constantly -- and constantly is roughly
 * eighteen times what Trafiklab asks for. Computing it here is what lets the
 * screen tick every ten seconds while the data behind it refreshes every
 * couple of minutes, which is exactly the trade they recommend.
 */
import { useSyncExternalStore } from 'react';

const TZ = 'Europe/Stockholm';

/** Beyond this, a countdown stops being useful and a clock time starts. */
const CLOCK_AFTER_MIN = 30;

const clockFormat = new Intl.DateTimeFormat('sv-SE', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const partsFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** How far Stockholm is from UTC at a given instant, DST included. */
function offsetAt(instant: number): number {
  const parts = partsFormat.formatToParts(new Date(instant));
  const at = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    at('year'),
    at('month') - 1,
    at('day'),
    at('hour'),
    at('minute'),
    at('second'),
  );
  return asUtc - instant;
}

/**
 * SL sends wall-clock times with no zone at all -- "2026-09-29T21:12:00" --
 * which JavaScript would read as the phone's local time. On a phone set to
 * Stockholm that is right by luck; anywhere else every countdown on the board
 * would be off by hours. So the string is read as the Stockholm wall clock it
 * actually is.
 *
 * Two passes: the first guess uses the offset around the wrong instant, which
 * only matters within an hour of a DST change, and the second corrects it.
 */
export function parseStockholm(iso: string): number {
  const naive = Date.parse(`${iso}Z`);
  if (!Number.isFinite(naive)) return NaN;
  const first = naive - offsetAt(naive);
  return naive - offsetAt(first);
}

export const clockAt = (instant: number): string =>
  clockFormat.format(new Date(instant));

/**
 * "Nu", "7 min", or a clock time -- the same shapes SL's own field used, so
 * the board reads as it always did.
 *
 * Rounded down, because a bus shown as "1 min" that leaves in 70 seconds is
 * a bus you can still catch, while one shown as "1 min" that leaves in 30 is
 * one you cannot.
 */
export function countdown(expected: string, now: number = Date.now()): string {
  const at = parseStockholm(expected);
  if (!Number.isFinite(at)) return '';

  const minutes = Math.floor((at - now) / 60_000);
  if (minutes <= 0) return 'Nu';
  if (minutes > CLOCK_AFTER_MIN) return clockAt(at);
  return `${minutes} min`;
}

/**
 * A heartbeat that re-renders whatever is showing a countdown.
 *
 * Only a trigger: the tick's own timestamp is deliberately not what the text
 * is computed from, because the ticker pauses while the page is hidden and
 * that timestamp would then be however old the last tick was. Rows read the
 * real clock at render time instead, so the first frame after the app comes
 * back is already right rather than right one tick later.
 *
 * One interval for the whole board rather than one per row, and none at all
 * while nobody is looking.
 */
const TICK_MS = 10_000;

let now = Date.now();
let timer: ReturnType<typeof setInterval> | undefined;
const listeners = new Set<() => void>();

function publish() {
  now = Date.now();
  for (const fn of listeners) fn();
}

function start() {
  if (timer !== undefined) return;
  timer = setInterval(publish, TICK_MS);
}

function stop() {
  clearInterval(timer);
  timer = undefined;
}

function onVisibility() {
  if (document.hidden) {
    stop();
  } else {
    publish();
    start();
  }
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  if (listeners.size === 1) {
    document.addEventListener('visibilitychange', onVisibility);
    if (!document.hidden) start();
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    }
  };
}

export function useClockTick(): void {
  useSyncExternalStore(
    subscribe,
    () => now,
    () => now,
  );
}

/** The clock time SL meant, for a departure that is not counting down. */
export const clockFor = (iso: string): string => {
  const at = parseStockholm(iso);
  return Number.isFinite(at) ? clockAt(at) : '';
};
